/** Save a Blob to the local filesystem with the best available API.
 *
 *  Resolution order:
 *   1. **File System Access API** (Chrome / Edge / desktop Safari 17+).
 *      Returns the resulting `FileSystemFileHandle` so callers can pin
 *      it on their tab model and let a follow-up Save write in-place
 *      via `handle.createWritable()` — no second picker dialog.
 *   2. **Web Share API on iOS** (Safari ignores `<a download>` and
 *      navigates to the blob URL instead of triggering a save dialog;
 *      the share sheet gives the user a real "Save to Files" option).
 *   3. **`<a download>` fallback** for everywhere else.
 *
 *  Callers need to distinguish three outcomes:
 *   - **saved-with-handle** — FSA picker succeeded; pin the handle on
 *     the tab so a follow-up Cmd+S can write in-place.
 *   - **saved** — bytes are on disk / in the share sheet pipeline,
 *     but there's no handle to pin (iOS Share, `<a download>`, or
 *     FSA-failed-mid-write fallback to `<a download>`).
 *   - **cancelled** — user dismissed the picker / share sheet without
 *     saving. Callers should suppress success toasts and not mark the
 *     document clean. */
export type SaveBlobLocallyResult =
  | { status: 'saved-with-handle'; handle: FileSystemFileHandle }
  | { status: 'saved' }
  | { status: 'cancelled' };

export interface SaveBlobLocallyOptions {
  /** Bare filename without extension — e.g. "Untitled". */
  fileName: string;
  /** Extension without the leading dot — e.g. "png", "psd". Used both
   *  to build the suggested name and to pre-filter the picker's
   *  format dropdown. */
  extension: string;
  /** Human-readable description for the picker's format dropdown —
   *  e.g. "PNG image", "PSD Document". */
  description: string;
}

export async function saveBlobLocally(
  blob: Blob,
  options: SaveBlobLocallyOptions,
): Promise<SaveBlobLocallyResult> {
  const { fileName, extension, description } = options;
  const fullName = `${fileName}.${extension}`;
  const mimeType = blob.type || 'application/octet-stream';

  // 1. File System Access API — only when both showSaveFilePicker and
  //    createWritable land in this browser. Some embedded webviews
  //    expose the picker but not createWritable (or vice versa); we
  //    feature-detect both before committing to this path.
  const fsa = window as typeof window & {
    showSaveFilePicker?: (init: {
      suggestedName?: string;
      types?: Array<{ description?: string; accept: Record<string, string[]> }>;
    }) => Promise<FileSystemFileHandle>;
  };
  if (typeof fsa.showSaveFilePicker === 'function') {
    try {
      const handle = await fsa.showSaveFilePicker({
        suggestedName: fullName,
        types: [
          {
            description,
            accept: { [mimeType]: [`.${extension}`] },
          },
        ],
      });
      const writable = await handle.createWritable();
      try {
        await writable.write(blob);
      } finally {
        // `close()` flushes — must run even if `write()` threw, or
        // the underlying file is left half-written on disk.
        await writable.close();
      }
      return { status: 'saved-with-handle', handle };
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { status: 'cancelled' };
      }
      // Other failures (e.g. user denied write permission after
      // picking, or the file system errored mid-write) — fall through
      // to the `<a download>` path so the user still ends up with a
      // file in their Downloads folder. Better than a silent failure.
      console.warn('[save-blob-locally] showSaveFilePicker failed:', err);
    }
  }

  // 2. iOS Safari — ignores `<a download>` and renders the blob URL
  //    in a new tab. The Web Share API surfaces "Save to Files" via
  //    the system share sheet.
  const isIOS =
    typeof navigator !== 'undefined' &&
    /iPad|iPhone|iPod/.test(navigator.userAgent) &&
    !('MSStream' in window);
  if (isIOS && typeof navigator !== 'undefined' && 'share' in navigator) {
    const file = new File([blob], fullName, { type: mimeType });
    const nav = navigator as Navigator & { canShare?: (data: ShareData) => boolean };
    if (!nav.canShare || nav.canShare({ files: [file] })) {
      try {
        await nav.share({ files: [file], title: fullName });
        return { status: 'saved' };
      } catch (shareErr) {
        // User dismissed the share sheet — same UX as cancelling the
        // FSA picker. Don't fall through; the user said no.
        if (shareErr instanceof Error && shareErr.name === 'AbortError') {
          return { status: 'cancelled' };
        }
        // Other share failures fall through to `<a download>`.
      }
    }
  }

  // 3. Fallback — synthesize an `<a download>` click. No handle, but
  //    the browser will commit the bytes to the Downloads folder
  //    without a user-cancellable dialog (so this branch is always
  //    "saved", never "cancelled").
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fullName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Defer revocation a tick so the browser's async download handler
  // has finished reading the blob — some Chrome / Edge versions open
  // the save dialog AFTER `click()` returns; revoking immediately
  // races the read on slow systems.
  setTimeout(() => URL.revokeObjectURL(url), 0);
  return { status: 'saved' };
}
